import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';
import { BusinessService } from '../services/business.service';
import { SubscriptionPlansService } from '../services/subscription-plans.service';
import { CurrentUserService } from '../auth/current-user.service';

export const businessOwnerOnlyGuard: CanActivateFn = () => {
  const currentUser = inject(CurrentUserService);
  const router = inject(Router);
  const businesses = inject(BusinessService);
  if (!currentUser.isBusinessOwner()) return router.createUrlTree([currentUser.businessLandingRoute()]);
  return businesses.myApprovalStatus().pipe(
    map((status) => status.businessKind === 'PlatformOwned'
      ? router.createUrlTree(['/business/dashboard'])
      : true),
    catchError(() => of(true)),
  );
};

export const businessOnboardingGuard: CanActivateFn = () => {
  const businesses = inject(BusinessService);
  const router = inject(Router);
  const plans = inject(SubscriptionPlansService);
  const currentUser = inject(CurrentUserService);

  // Invited staff inherit the already-approved business context. Onboarding and
  // plan selection are owner responsibilities only.
  if (!currentUser.isBusinessOwner()) return true;

  return businesses.myApprovalStatus().pipe(
    switchMap((status) => {
      // Admin-created internal businesses have their own protected plan and are
      // never required to submit an external-business application.
      if (status.businessKind === 'PlatformOwned') return of(true);
      if (status.approvalStatus !== 'Approved') return of(router.createUrlTree(['/business/onboarding']));
      return businesses.mine().pipe(
        switchMap((business) => {
          if (!business) return of(router.createUrlTree(['/business/onboarding']));
          return plans.mine().pipe(map((subscription) =>
            subscription?.status === 'Active' ? true : router.createUrlTree(['/business/plan-selection'])));
        }),
      );
    }),
    // Fail closed: an unavailable status API must never unlock business modules.
    catchError(() => of(router.createUrlTree(['/business/onboarding']))),
  );
};

export const businessPlanSelectionGuard: CanActivateFn = () => {
  const businesses = inject(BusinessService);
  const router = inject(Router);
  const currentUser = inject(CurrentUserService);

  if (!currentUser.isBusinessOwner()) {
    return router.createUrlTree([currentUser.businessLandingRoute()]);
  }

  return businesses.myApprovalStatus().pipe(
    switchMap((status) => {
      if (status.businessKind === 'PlatformOwned') return of(router.createUrlTree(['/business/dashboard']));
      if (status.approvalStatus !== 'Approved') return of(router.createUrlTree(['/business/onboarding']));
      return businesses.mine().pipe(map((business) => business
        ? true
        : router.createUrlTree(['/business/onboarding'])));
    }),
    catchError(() => of(router.createUrlTree(['/business/onboarding']))),
  );
};
